/**
 * Poseidon dashboard — Operations (§3.2 G).
 *
 * MT5 EA bridge status, last authenticated heartbeat and reconciliation result;
 * terminal/server/account metadata; scheduler state and next session cutoff; data
 * and DeepSeek health; event/audit logs; and the soft/hard halt controls.
 */
import { useState } from 'react';
import type { SystemEventRow } from '../api/types';
import { API_MODE, BUILD_ID, IS_MOCK_MODE, POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useDashboardEvent, useServices } from '../state/Services';
import { useRibbon } from '../components/AppShell';
import { DataTable } from '../components/DataTable';
import { ExecutionModeControl, HaltControls } from '../components/Controls';
import { ReadinessPanel } from '../components/ReadinessPanel';
import { ReconciliationSummary, SystemEventList } from '../components/SystemEventList';
import { Badge, ErrorState, KeyValueList, LoadingState, Panel, Unavailable } from '../components/primitives';
import { formatBytes, formatDateTimeWithZone, formatDuration, formatNumber } from '../lib/format';

export function Operations(): JSX.Element {
  const { api, isMock } = useServices();
  const ribbon = useRibbon();
  const [selectedEvent, setSelectedEvent] = useState<SystemEventRow | null>(null);

  const readiness = useApiData('ops-readiness', () => api.getReadiness(), { pollMs: POLL_INTERVALS.readiness, mode: api.mode });
  const bridge = useApiData('ops-bridge', () => api.getBridgeStatus(), { pollMs: POLL_INTERVALS.operations, mode: api.mode });
  const terminal = useApiData('ops-terminal', () => api.getTerminalMetadata(), { pollMs: POLL_INTERVALS.operations * 4, mode: api.mode });
  const scheduler = useApiData('ops-scheduler', () => api.getScheduler(), { pollMs: POLL_INTERVALS.operations, mode: api.mode });
  const health = useApiData('ops-health', () => api.getDataHealth(), { pollMs: POLL_INTERVALS.operations, mode: api.mode });
  const events = useApiData('ops-events', () => api.getSystemEvents(), { pollMs: POLL_INTERVALS.events, mode: api.mode });

  useDashboardEvent('system.event', () => events.refresh());
  useDashboardEvent('bridge.updated', () => bridge.refresh());

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Operations</h1>
          <div className="page-header__spec">§3.2 G · bridge, terminal metadata, scheduler, data health, event log and the halt controls</div>
        </div>
        <div className="data-clock">
          <span className="text-faint">build</span>
          <span className="text-mono">{BUILD_ID}</span>
          <span className="text-faint">data source</span>
          <Badge tone={IS_MOCK_MODE ? 'warn' : 'ok'}>{API_MODE}</Badge>
        </div>
      </header>

      {readiness.error !== null && readiness.data === null ? (
        <ErrorState error={readiness.error} context="Pre-flight readiness could not be loaded." onRetry={readiness.refresh} />
      ) : readiness.data === null ? (
        <LoadingState label="Loading readiness checks…" />
      ) : (
        <ReadinessPanel readiness={readiness.data} mock={isMock} />
      )}

      <Panel
        title="Execution mode"
        subtitle="§19.4 · enabling or disabling demo execution requires a typed confirmation"
        mock={isMock}
        footnote="The default is approval-required. §19.4 requires a separate mini-gate of clean demo lifecycles before constrained automated execution is switched on."
      >
        {ribbon === null ? <LoadingState label="Waiting for execution state…" /> : <ExecutionModeControl mode={ribbon.execution_mode} readiness={readiness.data} />}
      </Panel>

      <Panel
        title="Halt controls"
        subtitle="§7.3, §3.1 · soft halt and hard halt are visually and behaviourally distinct"
        mock={isMock}
      >
        {ribbon === null ? <LoadingState label="Waiting for halt state…" /> : <HaltControls halt={ribbon.halt} mock={isMock} />}
      </Panel>

      <div className="grid grid--2">
        <Panel title="EA bridge status" subtitle="§4.2 protocol, authentication and heartbeat" mock={isMock}>
          {bridge.error !== null && bridge.data === null ? (
            <ErrorState error={bridge.error} context="Bridge status could not be loaded." onRetry={bridge.refresh} />
          ) : bridge.data === null ? (
            <LoadingState label="Loading bridge status…" />
          ) : (
            <div className="grid" style={{ gap: 12 }}>
              <KeyValueList
                items={[
                  { key: 'EA', value: `${bridge.data.ea_name} · protocol ${bridge.data.protocol_version}` },
                  {
                    key: 'Authenticated',
                    value: (
                      <>
                        <Badge tone={bridge.data.authenticated ? 'ok' : 'danger'}>{bridge.data.authenticated ? 'yes' : 'no'}</Badge>{' '}
                        {bridge.data.authentication_method}
                      </>
                    ),
                  },
                  { key: 'Bridge token', value: bridge.data.token_present ? 'present in the daemon secret store (never sent to this browser)' : 'MISSING — the daemon must refuse to trade' },
                  { key: 'Last authenticated heartbeat', value: formatDateTimeWithZone(bridge.data.last_authenticated_heartbeat_at) },
                  { key: 'Heartbeat interval / missed', value: `${formatDuration(bridge.data.heartbeat_interval_ms)} · ${bridge.data.missed_heartbeats} missed` },
                  { key: 'Connected since', value: formatDateTimeWithZone(bridge.data.connected_since) },
                  { key: 'Remote address', value: <span className="text-mono">{bridge.data.remote_address}</span> },
                  { key: 'Sequence number', value: bridge.data.sequence_number === null ? <Unavailable /> : <span className="text-mono">{bridge.data.sequence_number}</span> },
                ]}
              />
              <ReconciliationSummary reconciliation={bridge.data.last_reconciliation} />
            </div>
          )}
        </Panel>

        <Panel title="Scheduler" subtitle="§3.2 G · session cutoff, flatten deadline and the next scheduled task" mock={isMock}>
          {scheduler.data === null ? (
            <LoadingState label="Loading scheduler…" />
          ) : (
            <div className="grid" style={{ gap: 12 }}>
              <KeyValueList
                items={[
                  { key: 'State', value: <Badge tone={scheduler.data.scheduler_state === 'running' ? 'ok' : scheduler.data.scheduler_state === 'error' ? 'danger' : 'muted'}>{scheduler.data.scheduler_state}</Badge> },
                  { key: 'Broker timezone', value: scheduler.data.broker_timezone },
                  { key: 'Broker time', value: <span className="text-mono">{formatDateTimeWithZone(scheduler.data.broker_time, scheduler.data.broker_timezone)}</span> },
                  { key: 'Session open', value: formatDateTimeWithZone(scheduler.data.session_open_at) },
                  { key: 'No-new-trades cutoff', value: formatDateTimeWithZone(scheduler.data.no_new_trades_at) },
                  { key: 'Flatten deadline', value: formatDateTimeWithZone(scheduler.data.flatten_deadline_at) },
                  { key: 'Session close', value: formatDateTimeWithZone(scheduler.data.session_close_at) },
                  { key: 'Last report', value: formatDateTimeWithZone(scheduler.data.last_report_at) },
                ]}
              />
              <div>
                <div className="ribbon__label">Upcoming tasks</div>
                <ul className="consequences" style={{ paddingLeft: 18 }}>
                  {scheduler.data.upcoming.map((task) => (
                    <li key={`${task.name}-${task.at}`}>
                      <span className="text-mono">{formatDateTimeWithZone(task.at)}</span> — {task.name} ({task.kind})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Data and service health" subtitle="§3.2 G · database, tick ingest, bar aggregation, DeepSeek, disk" mock={isMock} flush>
        {health.error !== null && health.data === null ? (
          <ErrorState error={health.error} context="Data health could not be loaded." onRetry={health.refresh} />
        ) : health.data === null ? (
          <LoadingState label="Loading data health…" />
        ) : (
          <div className="panel__body">
            <div className="grid grid--3">
              <Panel title="Database" subtitle="execution fails closed when this is down (§10.2)">
                <KeyValueList
                  items={[
                    { key: 'Reachable', value: <Badge tone={health.data.database.reachable ? 'ok' : 'danger'}>{health.data.database.reachable ? 'yes' : 'NO'}</Badge> },
                    { key: 'Latency', value: health.data.database.latency_ms === null ? <Unavailable /> : `${health.data.database.latency_ms} ms` },
                    { key: 'Schema version', value: <span className="text-mono">{health.data.database.schema_version ?? '—'}</span> },
                    { key: 'Migrations applied', value: health.data.database.migrations_applied ?? <Unavailable /> },
                  ]}
                />
              </Panel>
              <Panel title="Market data" subtitle="tick ingest and bar aggregation">
                <KeyValueList
                  items={[
                    { key: 'Last tick', value: formatDateTimeWithZone(health.data.tick_ingest.last_tick_at) },
                    { key: 'Ticks / min', value: formatNumber(health.data.tick_ingest.ticks_last_minute, 0) },
                    { key: 'Gaps last hour', value: health.data.tick_ingest.gaps_last_hour },
                    { key: 'Last bar', value: formatDateTimeWithZone(health.data.bar_aggregation.last_bar_at) },
                    { key: 'Symbols lagging', value: health.data.bar_aggregation.symbols_lagging.length === 0 ? 'none' : health.data.bar_aggregation.symbols_lagging.join(', ') },
                  ]}
                />
              </Panel>
              <Panel title="Research and host" subtitle="DeepSeek health, disk headroom">
                <KeyValueList
                  items={[
                    { key: 'DeepSeek', value: <Badge tone={health.data.deepseek.health === 'healthy' ? 'ok' : health.data.deepseek.health === 'degraded' ? 'warn' : 'muted'}>{health.data.deepseek.health}</Badge> },
                    { key: 'Last research ok', value: formatDateTimeWithZone(health.data.deepseek.last_ok_at) },
                    { key: 'Free disk', value: formatBytes(health.data.disk.free_bytes) },
                    { key: 'Log directory', value: formatBytes(health.data.disk.log_dir_bytes) },
                  ]}
                />
              </Panel>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Terminal, server and account metadata" subtitle="§3.2 G · recorded alongside test reports (§11)" mock={isMock} flush>
        {terminal.data === null ? (
          <LoadingState label="Loading terminal metadata…" />
        ) : (
          <>
            <div className="panel__body">
              <KeyValueList
                items={[
                  { key: 'Terminal build', value: terminal.data.terminal_build ?? <Unavailable /> },
                  { key: 'Broker', value: terminal.data.terminal_company ?? <Unavailable /> },
                  { key: 'Terminal path', value: <span className="text-mono">{terminal.data.terminal_path_redacted ?? '—'}</span> },
                  { key: 'Server', value: terminal.data.server_name },
                  { key: 'Server timezone', value: terminal.data.server_timezone },
                  { key: 'Account mode', value: <Badge tone={terminal.data.account_mode === 'demo' ? 'ok' : 'danger'}>{terminal.data.account_mode}</Badge> },
                  { key: 'Margin mode', value: terminal.data.margin_mode },
                  { key: 'Trading allowed (terminal / EA)', value: `${terminal.data.trade_allowed === null ? '—' : String(terminal.data.trade_allowed)} / ${terminal.data.ea_trade_allowed === null ? '—' : String(terminal.data.ea_trade_allowed)}` },
                ]}
              />
            </div>
            <DataTable
              rows={terminal.data.symbol_specs}
              rowKey={(row) => row.symbol}
              columns={[
                { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => <strong>{row.symbol}</strong> },
                { key: 'digits', header: 'Digits / point', align: 'right', render: (row) => `${row.digits} / ${row.point}` },
                { key: 'volume', header: 'Volume min / step / max', align: 'right', render: (row) => `${row.volume_min} / ${row.volume_step} / ${row.volume_max}` },
                { key: 'contract', header: 'Contract size', align: 'right', sortValue: (row) => row.contract_size, render: (row) => formatNumber(row.contract_size, 0) },
                { key: 'spread', header: 'Typical spread (pts)', align: 'right', sortValue: (row) => row.spread_typical_points, render: (row) => (row.spread_typical_points === null ? <Unavailable /> : formatNumber(row.spread_typical_points, 0)) },
                { key: 'swap', header: 'Swap long / short', align: 'right', render: (row) => <span className="text-mono">{row.swap_long ?? '—'} / {row.swap_short ?? '—'}</span> },
              ]}
              emptyTitle="No symbol specifications"
              footnote="Broker contract specifications drive lot sizing (§5.4). They must come from the terminal, never from a hard-coded assumption."
            />
          </>
        )}
      </Panel>

      <SystemEventList
        events={events.data ?? []}
        title="Event and audit log"
        subtitle="MT5 connection changes, data-quality failures, risk events, API errors and manual actions"
        mock={isMock}
        onRowClick={setSelectedEvent}
      />

      {selectedEvent !== null && (
        <Panel
          title="Event detail"
          subtitle={selectedEvent.event_id}
          actions={
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setSelectedEvent(null)}>
              Close
            </button>
          }
        >
          <KeyValueList
            items={[
              { key: 'Occurred at', value: formatDateTimeWithZone(selectedEvent.occurred_at) },
              { key: 'Kind / severity', value: `${selectedEvent.kind} / ${selectedEvent.severity}` },
              { key: 'Actor', value: <span className="text-mono">{selectedEvent.actor}</span> },
              { key: 'Symbol', value: selectedEvent.symbol ?? '—' },
              { key: 'Correlation ID', value: selectedEvent.correlation_id === null ? '—' : <span className="text-mono">{selectedEvent.correlation_id}</span> },
              { key: 'Summary', value: selectedEvent.summary },
              { key: 'Detail', value: selectedEvent.detail ?? '—' },
              { key: 'Redacted', value: selectedEvent.redacted ? 'yes — secrets and account identifiers are stripped before display (§11)' : 'no' },
            ]}
          />
        </Panel>
      )}

      <Panel title="Operational boundaries" subtitle="what this build will not do">
        <ul className="consequences" style={{ paddingLeft: 18 }}>
          <li>This dashboard cannot move the system to a live account. §7.1 hard-rejects any non-demo account mode.</li>
          <li>The emergency halt endpoint (protected by a separate local secret) is not reachable from this UI — that is deliberate (§7.3).</li>
          <li>{isMock ? 'In mock mode nothing here reflects a real MT5 terminal, bridge, database or DeepSeek account.' : 'Values come from the Rust daemon at 127.0.0.1:8000 over the same origin as this page.'}</li>
          <li>Account numbers are masked by the daemon before transmission; full account details never reach the browser (§11).</li>
        </ul>
      </Panel>
    </>
  );
}
